import type { CheckInPayload } from '@foyer/shared';

/**
 * Offline outbox.
 *
 * A check-in must survive everything that can realistically go wrong at a
 * reception desk: WLAN drop-out, backend restart, tablet reboot, browser tab
 * killed by iPadOS to reclaim memory. The submission is therefore written to
 * IndexedDB *before* the network is touched and only removed once the server
 * has confirmed it.
 *
 * IndexedDB, not localStorage: localStorage is synchronous (janky on a tablet),
 * limited to ~5 MB and, more importantly, this is personal data that must be
 * removable in one transaction when the retention rules demand it.
 */

const DB_NAME = 'foyer-kiosk';
const DB_VERSION = 1;
const STORE = 'outbox';

export interface OutboxEntry {
  idempotencyKey: string;
  payload: CheckInPayload;
  createdAt: number;
  attempts: number;
  lastError: string | null;
}

let dbPromise: Promise<IDBDatabase> | null = null;

function openDb(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE)) {
        const store = db.createObjectStore(STORE, { keyPath: 'idempotencyKey' });
        store.createIndex('createdAt', 'createdAt');
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
  return dbPromise;
}

async function tx<T>(mode: IDBTransactionMode, run: (store: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  const db = await openDb();
  return new Promise<T>((resolve, reject) => {
    const transaction = db.transaction(STORE, mode);
    const request = run(transaction.objectStore(STORE));
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
    transaction.onabort = () => reject(transaction.error);
  });
}

/** True when the browser can persist at all (private mode / locked-down kiosk). */
export async function isAvailable(): Promise<boolean> {
  try {
    await openDb();
    return true;
  } catch {
    return false;
  }
}

export async function enqueue(payload: CheckInPayload): Promise<void> {
  const entry: OutboxEntry = {
    idempotencyKey: payload.idempotencyKey,
    payload,
    createdAt: Date.now(),
    attempts: 0,
    lastError: null,
  };
  await tx('readwrite', (store) => store.put(entry));
}

export async function list(): Promise<OutboxEntry[]> {
  const all = await tx<OutboxEntry[]>('readonly', (store) => store.getAll() as IDBRequest<OutboxEntry[]>);
  return all.sort((a, b) => a.createdAt - b.createdAt);
}

export async function count(): Promise<number> {
  try {
    return await tx<number>('readonly', (store) => store.count());
  } catch {
    return 0;
  }
}

export async function remove(idempotencyKey: string): Promise<void> {
  await tx('readwrite', (store) => store.delete(idempotencyKey));
}

export async function markFailed(idempotencyKey: string, error: string): Promise<void> {
  const existing = await tx<OutboxEntry | undefined>(
    'readonly',
    (store) => store.get(idempotencyKey) as IDBRequest<OutboxEntry | undefined>,
  );
  if (!existing) return;
  await tx('readwrite', (store) =>
    store.put({ ...existing, attempts: existing.attempts + 1, lastError: error.slice(0, 200) }),
  );
}

/**
 * Discards entries the server rejected permanently (e.g. a form field that no
 * longer exists) and entries older than the retention window. Without this a
 * poisoned entry would be retried forever.
 */
export async function prune(maxAgeMs = 7 * 24 * 3600 * 1000): Promise<number> {
  const entries = await list();
  const cutoff = Date.now() - maxAgeMs;
  let removed = 0;
  for (const entry of entries) {
    if (entry.createdAt < cutoff) {
      await remove(entry.idempotencyKey);
      removed++;
    }
  }
  return removed;
}

export async function clear(): Promise<void> {
  await tx('readwrite', (store) => store.clear());
}
